/**
 * Card component — Brand Book §2.10
 * 5 variants: default, interactive, accent-left, stat, action (dashed)
 * bg-card, 1px solid --border, 0px radius
 * Default padding: 16px. Interactive hovers: shadow-sm + border-strong.
 * Accent-left: 3px --orange left border.
 * Action: dashed 2px border, centered content, muted colors.
 */

import { clsx } from 'clsx';
import type { ReactNode, MouseEventHandler } from 'react';

export interface CardProps {
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'interactive' | 'accent-left' | 'stat' | 'action';
  /**
   * Accent bar color for `variant="accent-left"`. Defaults to orange (brand).
   * 'red' was added for the accountant portal "Needs attention" urgency state
   * (StatsRow / AttentionQueue) — no other call sites consume it yet.
   */
  accentColor?: 'orange' | 'red';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  shadow?: boolean;
  hover?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
  /** Test hook forwarded to the root element (optional, no visual effect). */
  'data-testid'?: string;
}

export function Card({
  children,
  className,
  variant = 'default',
  accentColor = 'orange',
  padding = 'md',
  shadow = false,
  hover = false,
  onClick,
  'data-testid': dataTestId,
}: CardProps) {
  const paddingSizes = {
    none: '',
    sm: 'p-3',      // 12px — compact
    md: 'p-4',      // 16px — default per spec
    lg: 'p-6',      // 24px — spacious
  };

  const isInteractive = variant === 'interactive' || hover || !!onClick;

  const accentBorderClass = accentColor === 'red' ? 'border-l-red-500' : 'border-l-orange';

  const variantStyles = {
    default: 'bg-surface-card border border-border',
    interactive: clsx(
      'bg-surface-card border border-border cursor-pointer',
      'transition-[box-shadow,border-color] duration-200 ease',
      'hover:shadow-sm hover:border-border-strong'
    ),
    'accent-left': clsx(
      'bg-surface-card border border-border border-l-[3px]',
      accentBorderClass
    ),
    stat: 'bg-surface-card border border-border',
    action: clsx(
      'bg-transparent border-2 border-dashed border-border cursor-pointer',
      'text-txt-tertiary',
      'transition-[border-color] duration-200 ease',
      'hover:border-border-strong hover:text-txt-secondary',
      'flex items-center justify-center'
    ),
  };

  return (
    <div
      className={clsx(
        variantStyles[variant],
        variant === 'default' && isInteractive &&
          'cursor-pointer transition-[box-shadow,border-color] duration-200 ease hover:shadow-sm hover:border-border-strong',
        shadow && 'shadow-sm',
        paddingSizes[padding],
        className
      )}
      onClick={onClick}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
}

// Card Header
export interface CardHeaderProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function CardHeader({ title, subtitle, icon, action, className }: CardHeaderProps) {
  return (
    <div className={clsx('flex items-start justify-between', className)}>
      <div className="flex items-center gap-2">
        {icon}
        <div>
          <h3 className="text-h3 text-txt-primary">{title}</h3>
          {subtitle && (
            <p className="mt-0.5 text-body-sm text-txt-tertiary">{subtitle}</p>
          )}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}